import type {
  DocumentMapData,
  DocumentMapEdge,
  DocumentMapEdgeType,
  DocumentMapNode,
  DocumentMapNodeClass,
  DocumentRole,
} from "@/types/document-map";
import { pluralize } from "@/helpers/pluralize";
import { safeExternalUrl } from "@/helpers/safeExternalUrl";
import type { ClassStyle } from "./types";

export type MapLinkView = "structure" | "all";

export interface GraphOptions {
  view: MapLinkView;
  showRedundant: boolean;
}

export interface OnlineTermsLink {
  label: string;
  url: string | null;
}

export interface GraphNode {
  id: string;
  label: string;
  nodeClass: DocumentMapNodeClass;
  source: DocumentMapNode;
  parentUnclear: boolean;
  lowConfidence: boolean;
  faded: boolean;
  inoperative: boolean;
  isGoverningOnlineTerms: boolean;
  /** The online policies this box stands for, or null for an ordinary box. */
  collapsedPolicies: OnlineTermsLink[] | null;
}

export interface GraphGroup {
  id: string;
  label: string;
  isMissing: boolean;
  nodeIds: string[];
  /** [parent, child] pairs the layout places; drawn lines come from `edges`. */
  links: [string, string][];
}

export interface DocumentGraph {
  nodes: GraphNode[];
  groups: GraphGroup[];
  unplaced: GraphNode[];
  edges: DocumentMapEdge[];
}

export const NODE_CLASS_STYLES: Record<DocumentMapNodeClass, ClassStyle> = {
  FRAMEWORK: { label: "Framework", fill: "bg-[#DCE4EF]", border: "border-[#9FB2CB]", text: "text-[#1F3B5C]" },
  STANDALONE: { label: "Standalone", fill: "bg-[#DCE4EF]", border: "border-[#9FB2CB]", text: "text-[#1F3B5C]" },
  ORDER: { label: "Order", fill: "bg-[#E4EDF8]", border: "border-[#D3DEEC]", text: "text-[#1D4A7C]" },
  MODIFICATION: { label: "Amendment", fill: "bg-[#FBE7D2]", border: "border-[#E9C9A6]", text: "text-[#7E420A]" },
  SUPPORTING: { label: "Supporting", fill: "bg-[#DDF0EC]", border: "border-[#A9D3CC]", text: "text-[#145C54]" },
  NOTICE: { label: "Notice", fill: "bg-[#F8E6EE]", border: "border-[#EBC7D6]", text: "text-[#8A2350]" },
  EVIDENCE: { label: "Evidence", fill: "bg-[#EFECE7]", border: "border-[#DDD8D1]", text: "text-[#4A4741]" },
  ONLINE_TERMS: { label: "Online terms", fill: "bg-[#ECE9FA]", border: "border-[#CFC8F0]", text: "text-[#45399A]" },
  PLACEHOLDER: { label: "Missing", fill: "bg-[#F7F5F2]", border: "border-[#8A857D]", text: "text-[#3B3934]" },
  UNKNOWN: { label: "Unknown", fill: "bg-[#EFECE7]", border: "border-[#DDD8D1]", text: "text-[#4A4741]" },
};

const FALLBACK_ROLE_STYLES: Record<NonNullable<DocumentRole["family_role"]>, ClassStyle> = {
  PRIMARY: { ...NODE_CLASS_STYLES.FRAMEWORK, label: "Primary agreement" },
  CHILD: NODE_CLASS_STYLES.ORDER,
  MODIFIER: NODE_CLASS_STYLES.MODIFICATION,
  ANCILLARY: NODE_CLASS_STYLES.SUPPORTING,
  NOTICE: NODE_CLASS_STYLES.NOTICE,
  NOT_APPLICABLE: { ...NODE_CLASS_STYLES.UNKNOWN, label: "Other" },
  UNSURE: NODE_CLASS_STYLES.UNKNOWN,
};

export const EDGE_COLORS = {
  structure: "#5E6B7A",
  modifies: "#B9650F",
  supplements: "#5B4BB7",
  reference: "#8A8378",
} as const;

export const LOW_CONFIDENCE_DASH = "6 4";
export const DOTTED_DASH = "2 4";
const NON_HIERARCHICAL: ReadonlySet<DocumentMapEdgeType> = new Set(["REFERENCES", "EVIDENCES", "VERSION_OF"]);
const UNSIGNED_STATUSES: ReadonlySet<string> = new Set(["draft", "final - unsigned"]);
const MAX_SHORT_URL = 40;
const MIN_COLLAPSED_POLICIES = 3;

const isUnsigned = (status: string | null) =>
  status !== null && UNSIGNED_STATUSES.has(status.toLowerCase().replace(/[–—]/g, "-").trim());

/**
 * OT-13..15: more than two policies supplementing one node become one box, here and not in the prompt.
 * Online terms with any other link stay separate, so governing terms are never collapsed (OT-14).
 */
const collapseOnlinePolicies = (data: DocumentMapData) => {
  const supplementersByTarget = new Map<string, Set<string>>();
  for (const node of data.nodes) {
    if (node.node_class !== "ONLINE_TERMS") continue;
    const links = data.edges.filter(edge => edge.from === node.id || edge.to === node.id);
    if (!links.every(edge => edge.from === node.id && edge.type === "SUPPLEMENTS")) continue;
    for (const { to } of links) {
      supplementersByTarget.set(to, (supplementersByTarget.get(to) ?? new Set()).add(node.id));
    }
  }

  const nodes = new Map(data.nodes.map(node => [node.id, node]));
  let edges = data.edges;
  const collapsedPolicies = new Map<string, OnlineTermsLink[]>();
  const mostSupplementedFirst = [...supplementersByTarget].sort(([, a], [, b]) => b.size - a.size);
  for (const [target, supplementers] of mostSupplementedFirst) {
    // A policy supplementing several nodes goes into the first box only; the box then links to them all.
    const candidates = [...supplementers].flatMap(id => nodes.get(id) ?? []);
    if (candidates.length < MIN_COLLAPSED_POLICIES) continue;
    const id = `policies:${target}`;
    const candidateIds = new Set(candidates.map(node => node.id));
    const boxEdges = edges.filter(edge => candidateIds.has(edge.from));
    candidates.forEach(node => nodes.delete(node.id));
    nodes.set(id, {
      ...candidates[0],
      id,
      label: `Online policies (${candidates.length})`,
      url: null,
      referenced_by: [target],
    });
    collapsedPolicies.set(
      id,
      candidates.map(({ label, url }) => ({ label, url })),
    );
    const boxEdgeByTarget = new Map<string, DocumentMapEdge>();
    for (const edge of boxEdges) {
      const current = boxEdgeByTarget.get(edge.to);
      boxEdgeByTarget.set(edge.to, {
        ...(current ?? edge),
        id: `${id}:${edge.to}`,
        from: id,
        is_primary: edge.to === target && (edge.is_primary || current?.is_primary === true),
      });
    }
    edges = [...edges.filter(edge => !candidateIds.has(edge.from)), ...boxEdgeByTarget.values()];
  }
  return { nodes: [...nodes.values()], edges, collapsedPolicies };
};

export const buildDocumentGraph = (data: DocumentMapData, options: GraphOptions): DocumentGraph => {
  const { nodes: mapNodes, edges: allEdges, collapsedPolicies } = collapseOnlinePolicies(data);
  const primaryByChild = new Map(allEdges.filter(edge => edge.is_primary).map(edge => [edge.from, edge]));
  const childrenOf = new Map<string, string[]>();
  for (const edge of primaryByChild.values()) {
    childrenOf.set(edge.to, [...(childrenOf.get(edge.to) ?? []), edge.from]);
  }
  const hasChildren = (id: string) => (childrenOf.get(id)?.length ?? 0) > 0;

  const nodes: GraphNode[] = mapNodes.map(node => {
    const primary = primaryByChild.get(node.id);
    return {
      id: node.id,
      label: node.label,
      nodeClass: node.node_class,
      source: node,
      parentUnclear: node.placement === "UNPLACED" && !hasChildren(node.id),
      lowConfidence: primary?.confidence === "Low",
      faded: isUnsigned(node.status),
      inoperative: node.operative === false,
      isGoverningOnlineTerms:
        node.node_class === "ONLINE_TERMS" && allEdges.some(edge => edge.to === node.id && edge.type === "GOVERNED_BY"),
      collapsedPolicies: collapsedPolicies.get(node.id) ?? null,
    };
  });
  const byId = new Map(mapNodes.map(node => [node.id, node]));

  const topLevel = mapNodes.filter(
    node => !primaryByChild.has(node.id) && !(node.placement === "UNPLACED" && !hasChildren(node.id)),
  );
  // A missing or online-terms node nothing sits under is not a family of its own: it stays with
  // the node that cites it instead of drawing an empty "agreement missing" group.
  const satelliteLink = (node: DocumentMapNode) => {
    if (hasChildren(node.id) || (node.node_class !== "PLACEHOLDER" && node.node_class !== "ONLINE_TERMS")) {
      return undefined;
    }
    return allEdges.find(edge => edge.from === node.id || edge.to === node.id);
  };
  const satelliteAnchor = (node: DocumentMapNode) => {
    const link = satelliteLink(node);
    return link && (link.from === node.id ? link.to : link.from);
  };

  const roots = topLevel.filter(node => satelliteAnchor(node) === undefined);
  const groupOf = new Map<string, GraphGroup>();
  const groups = roots.map(root => {
    const group: GraphGroup = { id: root.id, label: "", isMissing: false, nodeIds: [], links: [] };
    const visit = (id: string) => {
      if (groupOf.has(id)) return;
      groupOf.set(id, group);
      group.nodeIds.push(id);
      for (const child of childrenOf.get(id) ?? []) {
        if (!groupOf.has(child)) group.links.push([id, child]);
        visit(child);
      }
    };
    visit(root.id);
    return group;
  });

  const satellites = topLevel.filter(node => satelliteAnchor(node) !== undefined);
  const stranded: DocumentMapNode[] = [];
  for (const satellite of satellites) {
    const anchor = satelliteAnchor(satellite)!;
    const group = groupOf.get(anchor);
    if (group === undefined) {
      stranded.push(satellite);
      continue;
    }
    groupOf.set(satellite.id, group);
    group.nodeIds.push(satellite.id);
    // Terms a document sits under go above it (OT-10); anything else hangs below what cites it.
    const link = satelliteLink(satellite)!;
    const sitsAbove = link.to === satellite.id && !NON_HIERARCHICAL.has(link.type);
    group.links.push(sitsAbove ? [satellite.id, anchor] : [anchor, satellite.id]);
  }
  // Cited only by a document in the Unplaced lane: it still needs a place to be drawn.
  for (const node of stranded) {
    const group: GraphGroup = { id: node.id, label: "", isMissing: false, nodeIds: [node.id], links: [] };
    groupOf.set(node.id, group);
    groups.push(group);
  }

  for (const group of groups) {
    const root = byId.get(group.id)!;
    const members = group.nodeIds.map(id => byId.get(id)!);
    const entity =
      root.purchaser ?? members.find(member => member.purchaser)?.purchaser ?? data.family.purchaser ?? null;
    group.isMissing = root.node_class === "PLACEHOLDER";
    const name = (entity ?? (group.isMissing ? "" : root.label)).toUpperCase();
    group.label = group.isMissing ? (name ? `${name} · AGREEMENT MISSING` : "AGREEMENT MISSING") : name;
  }

  const edges = allEdges.filter(edge => {
    if (options.view === "structure") return edge.is_primary;
    return options.showRedundant || !edge.redundant;
  });

  return {
    nodes,
    groups,
    unplaced: nodes.filter(node => !groupOf.has(node.id)),
    edges,
  };
};

export interface EdgeLineStyle {
  color: string;
  width: number;
  dash: string | null;
}

export const edgeLineStyle = (edge: DocumentMapEdge, touchesOnlineTerms: boolean): EdgeLineStyle => {
  const lowConfidenceDash = edge.confidence === "Low" ? LOW_CONFIDENCE_DASH : null;
  if (NON_HIERARCHICAL.has(edge.type)) {
    return { color: EDGE_COLORS.reference, width: 2, dash: lowConfidenceDash ?? DOTTED_DASH };
  }
  if (edge.type === "MODIFIES") {
    return { color: EDGE_COLORS.modifies, width: edge.is_primary ? 3 : 1, dash: lowConfidenceDash };
  }
  const color = edge.type === "SUPPLEMENTS" && touchesOnlineTerms ? EDGE_COLORS.supplements : EDGE_COLORS.structure;
  return { color, width: edge.is_primary ? 2 : 1, dash: lowConfidenceDash };
};

export const edgeTypeLabel = (type: DocumentMapEdgeType) => type.replaceAll("_", " ");

export const nodeDetailLine = (
  node: DocumentMapNode,
  lowConfidence: boolean,
  formatDate: (date: string) => string,
): string => {
  const date = node.date ? formatDate(node.date) : null;
  const parts = node.reference ? [node.reference, date] : [date, node.status];
  return [...parts, lowConfidence ? "Low confidence" : null].filter(Boolean).join(" · ");
};

/** OT-05: domain and path for display; the stored URL stays as extracted (OT-20). */
export const shortenUrl = (url: string): string => {
  const display = url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").split(/[?#]/)[0];
  return display.length > MAX_SHORT_URL ? `${display.slice(0, MAX_SHORT_URL - 1)}…` : display;
};

/** Every address a box stands for: its own, or those of the policies collapsed into it. */
export const onlineTermsLinks = (node: GraphNode): OnlineTermsLink[] =>
  node.collapsedPolicies ?? (node.source.url ? [{ label: node.label, url: node.source.url }] : []);

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * OT-19: only http and https open, so anything else stays text. An address written without a scheme, such
 * as "legal.vendor.com/msa", opens over https; what the user reads stays as extracted (OT-20).
 */
export const linkableUrl = (url: string): string | undefined => {
  const trimmed = url.trim();
  return safeExternalUrl(HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`);
};

export const documentRoleLabel = (role: DocumentRole | undefined): ClassStyle | null => {
  if (role?.node_class) return NODE_CLASS_STYLES[role.node_class];
  if (role?.family_role) return FALLBACK_ROLE_STYLES[role.family_role];
  return null;
};

/** OT-16: said only where online terms govern the documents at the top of a group. */
const onlineTermsSummary = (graph: DocumentGraph): string | null => {
  const rootIds = new Set(graph.groups.map(group => group.id));
  const online = graph.nodes.filter(node => node.nodeClass === "ONLINE_TERMS");
  const agreements = online.filter(node => node.isGoverningOnlineTerms && rootIds.has(node.id));
  if (agreements.length === 0) return null;
  const policies = online
    .filter(node => !agreements.includes(node))
    .reduce((total, node) => total + (node.collapsedPolicies?.length ?? 1), 0);
  return [
    "Governed by online terms",
    pluralize(agreements.length, "online agreement", "online agreements"),
    policies > 0 ? pluralize(policies, "online policy", "online policies") : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const groupCountLabel = (graph: DocumentGraph): string => pluralize(graph.groups.length, "group", "groups");

/** MV-28: groups, missing agreements and unplaced documents. */
export const graphStructureSummary = (graph: DocumentGraph): string => {
  const online = onlineTermsSummary(graph);
  if (online) return online;
  const missing = graph.groups.filter(group => group.isMissing).length;
  return [
    groupCountLabel(graph),
    missing > 0 ? pluralize(missing, "missing agreement", "missing agreements") : null,
    graph.unplaced.length > 0 ? `${graph.unplaced.length} unplaced` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** SV-17, OT-16: the informational line of the status banner. */
export const mapBannerSummary = (graph: DocumentGraph): string => {
  const online = onlineTermsSummary(graph);
  if (online) return online;
  const missing = graph.nodes.filter(node => node.nodeClass === "PLACEHOLDER").length;
  return missing > 0
    ? `Map · ${pluralize(missing, "referenced document", "referenced documents")} missing`
    : "Map · No referenced documents missing";
};

/** MV-29: the line above the map. */
export const documentCountsLine = (graph: DocumentGraph, excludedCount: number): string => {
  const shown = graph.nodes.filter(node => node.source.document_name !== null).length;
  return `${pluralize(shown, "document", "documents")} shown · ${excludedCount} excluded as not relevant`;
};
